"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Alert,
  Spinner,
  UncontrolledTooltip,
  ButtonGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import Api from "../../api/Api";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const HelpSupport = () => {
  const token = useSelector((state) => state.login.token);
  // State management
  const [faqs, setFaqs] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [appSettings, setAppSettings] = useState({
    help_footer_text: "",
    app_version: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Modal states
  const [faqModal, setFaqModal] = useState(false);
  const [contactModal, setContactModal] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);

  // Form states
  const [currentFaq, setCurrentFaq] = useState({
    id: null,
    question: "",
    answer: "",
    sort_order: 0,
    is_active: true,
  });

  const [currentContact, setCurrentContact] = useState({
    id: null,
    title: "",
    subtitle: "",
    icon: "faPhone",
    color: "#4358a6",
    action_type: "tel",
    action_value: "",
    sort_order: 0,
    is_active: true,
  });

  // Icon options for contact methods
  const iconOptions = [
    { value: "faPhone", label: "Phone", icon: "📞" },
    { value: "faEnvelope", label: "Email", icon: "✉️" },
    { value: "faComments", label: "Chat", icon: "💬" },
    { value: "faGlobe", label: "Website", icon: "🌐" },
    { value: "faHeadset", label: "Support", icon: "🎧" },
  ];

  // Action type options
  const actionTypeOptions = [
    { value: "tel", label: "Phone Call" },
    { value: "mailto", label: "Email" },
    { value: "url", label: "Website URL" },
    { value: "alert", label: "Alert Message" },
  ];

  // Load data on component mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadFAQs(), loadContactOptions(), loadAppSettings()]);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load help support data");
    } finally {
      setLoading(false);
    }
  };

  const loadFAQs = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/admin/help-support/faqs");
      if (response.data.success) {
        setFaqs(response.data.data);
      }
    } catch (error) {
      console.log(error.response);

      console.error("Error loading FAQs:", error);
      throw error;
    }
  };

  const loadContactOptions = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/admin/help-support/contact-options");
      if (response.data.success) {
        setContactOptions(response.data.data);
      }
    } catch (error) {
      console.error("Error loading contact options:", error);
      throw error;
    }
  };

  const loadAppSettings = async () => {
    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.get("/help-support");
      if (response.data.success) {
        setAppSettings({
          help_footer_text: response.data.data.footer_text,
          app_version: response.data.data.app_version,
        });
      }
    } catch (error) {
      console.error("Error loading app settings:", error);
      throw error;
    }
  };

  // FAQ Management
  const handleFaqSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = currentFaq.id
        ? `/admin/help-support/faqs/${currentFaq.id}`
        : "/admin/help-support/faqs";

      const method = currentFaq.id ? "put" : "post";
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api[method](url, currentFaq);

      if (response.data.success) {
        toast.success(response.data.message);
        setFaqModal(false);
        resetFaqForm();
        loadFAQs();
      }
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.error("Failed to save FAQ");
    } finally {
      setSaving(false);
    }
  };

  const handleEditFaq = (faq) => {
    setCurrentFaq(faq);
    setFaqModal(true);
  };

  const handleDeleteFaq = async (id) => {
    if (window.confirm("Are you sure you want to delete this FAQ?")) {
      try {
        // Set authorization header
        Api.defaults.headers.common["Authorization"] = "Bearer " + token;
        const response = await Api.delete(`/admin/help-support/faqs/${id}`);
        if (response.data.success) {
          toast.success("FAQ deleted successfully");
          loadFAQs();
        }
      } catch (error) {
        console.error("Error deleting FAQ:", error);
        toast.error("Failed to delete FAQ");
      }
    }
  };

  const resetFaqForm = () => {
    setCurrentFaq({
      id: null,
      question: "",
      answer: "",
      sort_order: 0,
      is_active: true,
    });
  };

  // Contact Options Management
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = currentContact.id
        ? `/admin/help-support/contact-options/${currentContact.id}`
        : "/admin/help-support/contact-options";

      const method = currentContact.id ? "put" : "post";
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api[method](url, currentContact);

      if (response.data.success) {
        toast.success(response.data.message);
        setContactModal(false);
        resetContactForm();
        loadContactOptions();
      }
    } catch (error) {
      console.error("Error saving contact option:", error);
      toast.error("Failed to save contact option");
    } finally {
      setSaving(false);
    }
  };

  const handleEditContact = (contact) => {
    setCurrentContact(contact);
    setContactModal(true);
  };

  const handleDeleteContact = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this contact option?")
    ) {
      try {
        // Set authorization header
        Api.defaults.headers.common["Authorization"] = "Bearer " + token;
        const response = await Api.delete(
          `/admin/help-support/contact-options/${id}`
        );
        if (response.data.success) {
          toast.success("Contact option deleted successfully");
          loadContactOptions();
        }
      } catch (error) {
        console.error("Error deleting contact option:", error);
        toast.error("Failed to delete contact option");
      }
    }
  };

  const resetContactForm = () => {
    setCurrentContact({
      id: null,
      title: "",
      subtitle: "",
      icon: "faPhone",
      color: "#4358a6",
      action_type: "tel",
      action_value: "",
      sort_order: 0,
      is_active: true,
    });
  };

  // App Settings Management
  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Set authorization header
      Api.defaults.headers.common["Authorization"] = "Bearer " + token;
      const response = await Api.put(
        "/admin/help-support/settings",
        appSettings
      );
      if (response.data.success) {
        toast.success("Settings updated successfully");
        setSettingsModal(false);
      }
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const truncateText = (text, maxLength = 100) => {
    return text.length > maxLength
      ? text.substring(0, maxLength) + "..."
      : text;
  };

  if (loading) {
    return (
      <Container fluid>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <Spinner size="lg" color="primary" />
          <span className="ml-3">Loading help support data...</span>
        </div>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Help & Support Management"
            breadcrumbItem="Help & Support Management"
          />

          {/* FAQ Management Section */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Frequently Asked Questions</h5>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => {
                      resetFaqForm();
                      setFaqModal(true);
                    }}
                  >
                    Add New FAQ
                  </Button>
                </CardHeader>
                <CardBody>
                  {faqs.length === 0 ? (
                    <Alert color="info">
                      No FAQs found. Add your first FAQ to get started.
                    </Alert>
                  ) : (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Question</th>
                          <th>Answer</th>
                          <th>Status</th>
                          <th>Order</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {faqs.map((faq) => (
                          <tr key={faq.id}>
                            <td style={{ maxWidth: "300px" }}>
                              <strong>{faq.question}</strong>
                            </td>
                            <td style={{ maxWidth: "400px" }}>
                              {truncateText(faq.answer)}
                            </td>
                            <td>
                              <Badge
                                color={faq.is_active ? "success" : "secondary"}
                              >
                                {faq.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>{faq.sort_order}</td>
                            <td>
                              <ButtonGroup size="sm">
                                <Button
                                  color="info"
                                  onClick={() => handleEditFaq(faq)}
                                  id={`edit-faq-${faq.id}`}
                                >
                                  Edit
                                </Button>
                                <UncontrolledTooltip
                                  target={`edit-faq-${faq.id}`}
                                >
                                  Edit FAQ
                                </UncontrolledTooltip>
                                <Button
                                  color="danger"
                                  onClick={() => handleDeleteFaq(faq.id)}
                                  id={`delete-faq-${faq.id}`}
                                >
                                  Delete
                                </Button>
                                <UncontrolledTooltip
                                  target={`delete-faq-${faq.id}`}
                                >
                                  Delete FAQ
                                </UncontrolledTooltip>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Contact Support Options Section */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Contact Support Options</h5>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => {
                      resetContactForm();
                      setContactModal(true);
                    }}
                  >
                    Add Contact Option
                  </Button>
                </CardHeader>
                <CardBody>
                  {contactOptions.length === 0 ? (
                    <Alert color="info">
                      No contact options found. Add your first contact option.
                    </Alert>
                  ) : (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Subtitle</th>
                          <th>Icon</th>
                          <th>Color</th>
                          <th>Action Type</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contactOptions.map((option) => (
                          <tr key={option.id}>
                            <td>
                              <strong>{option.title}</strong>
                            </td>
                            <td>{option.subtitle}</td>
                            <td>
                              {iconOptions.find(
                                (ico) => ico.value === option.icon
                              )?.icon || "📱"}
                            </td>
                            <td>
                              <div
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  backgroundColor: option.color,
                                  borderRadius: "50%",
                                  display: "inline-block",
                                }}
                              />
                              <span className="ml-2">{option.color}</span>
                            </td>
                            <td>
                              <Badge color="info">
                                {
                                  actionTypeOptions.find(
                                    (act) => act.value === option.action_type
                                  )?.label
                                }
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                color={
                                  option.is_active ? "success" : "secondary"
                                }
                              >
                                {option.is_active ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                            <td>
                              <ButtonGroup size="sm">
                                <Button
                                  color="info"
                                  onClick={() => handleEditContact(option)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  color="danger"
                                  onClick={() => handleDeleteContact(option.id)}
                                >
                                  Delete
                                </Button>
                              </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* App Settings Section */}
          <Row className="mb-4">
            <Col>
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">App Settings</h5>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setSettingsModal(true)}
                  >
                    Edit Settings
                  </Button>
                </CardHeader>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <strong>Footer Text:</strong>
                      <p className="text-muted">
                        {appSettings.help_footer_text || "Not set"}
                      </p>
                    </Col>
                    <Col md={6}>
                      <strong>App Version:</strong>
                      <p className="text-muted">
                        {appSettings.app_version || "Not set"}
                      </p>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* FAQ Modal */}
          <Modal
            isOpen={faqModal}
            toggle={() => setFaqModal(!faqModal)}
            size="lg"
          >
            <Form onSubmit={handleFaqSubmit}>
              <ModalHeader toggle={() => setFaqModal(!faqModal)}>
                {currentFaq.id ? "Edit FAQ" : "Add New FAQ"}
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label for="question">Question *</Label>
                  <Input
                    type="text"
                    id="question"
                    value={currentFaq.question}
                    onChange={(e) =>
                      setCurrentFaq({ ...currentFaq, question: e.target.value })
                    }
                    required
                    placeholder="Enter the FAQ question"
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="answer">Answer *</Label>
                  <Input
                    type="textarea"
                    id="answer"
                    rows="4"
                    value={currentFaq.answer}
                    onChange={(e) =>
                      setCurrentFaq({ ...currentFaq, answer: e.target.value })
                    }
                    required
                    placeholder="Enter the FAQ answer"
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="sort_order">Sort Order</Label>
                      <Input
                        type="number"
                        id="sort_order"
                        value={currentFaq.sort_order}
                        onChange={(e) =>
                          setCurrentFaq({
                            ...currentFaq,
                            sort_order: Number.parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="0"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mt-4">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={currentFaq.is_active}
                          onChange={(e) =>
                            setCurrentFaq({
                              ...currentFaq,
                              is_active: e.target.checked,
                            })
                          }
                        />
                        Active
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? (
                    <Spinner size="sm" />
                  ) : currentFaq.id ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
                <Button color="secondary" onClick={() => setFaqModal(false)}>
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Contact Option Modal */}
          <Modal
            isOpen={contactModal}
            toggle={() => setContactModal(!contactModal)}
            size="lg"
          >
            <Form onSubmit={handleContactSubmit}>
              <ModalHeader toggle={() => setContactModal(!contactModal)}>
                {currentContact.id
                  ? "Edit Contact Option"
                  : "Add Contact Option"}
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="title">Title *</Label>
                      <Input
                        type="text"
                        id="title"
                        value={currentContact.title}
                        onChange={(e) =>
                          setCurrentContact({
                            ...currentContact,
                            title: e.target.value,
                          })
                        }
                        required
                        placeholder="e.g., Phone Support"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="subtitle">Subtitle *</Label>
                      <Input
                        type="text"
                        id="subtitle"
                        value={currentContact.subtitle}
                        onChange={(e) =>
                          setCurrentContact({
                            ...currentContact,
                            subtitle: e.target.value,
                          })
                        }
                        required
                        placeholder="e.g., +963 11 123 4567"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="icon">Icon</Label>
                      <Input
                        type="select"
                        id="icon"
                        value={currentContact.icon}
                        onChange={(e) =>
                          setCurrentContact({
                            ...currentContact,
                            icon: e.target.value,
                          })
                        }
                      >
                        {iconOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.icon} {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="color">Color</Label>
                      <Input
                        type="color"
                        id="color"
                        value={currentContact.color}
                        onChange={(e) =>
                          setCurrentContact({
                            ...currentContact,
                            color: e.target.value,
                          })
                        }
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="action_type">Action Type</Label>
                      <Input
                        type="select"
                        id="action_type"
                        value={currentContact.action_type}
                        onChange={(e) =>
                          setCurrentContact({
                            ...currentContact,
                            action_type: e.target.value,
                          })
                        }
                      >
                        {actionTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="action_value">Action Value *</Label>
                  <Input
                    type="text"
                    id="action_value"
                    value={currentContact.action_value}
                    onChange={(e) =>
                      setCurrentContact({
                        ...currentContact,
                        action_value: e.target.value,
                      })
                    }
                    required
                    placeholder={
                      currentContact.action_type === "tel"
                        ? "+963111234567"
                        : currentContact.action_type === "mailto"
                        ? "support@tafaseel.com"
                        : currentContact.action_type === "url"
                        ? "https://www.tafaseel.com"
                        : "Coming Soon message"
                    }
                  />
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="sort_order">Sort Order</Label>
                      <Input
                        type="number"
                        id="sort_order"
                        value={currentContact.sort_order}
                        onChange={(e) =>
                          setCurrentContact({
                            ...currentContact,
                            sort_order: Number.parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="0"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup check className="mt-4">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={currentContact.is_active}
                          onChange={(e) =>
                            setCurrentContact({
                              ...currentContact,
                              is_active: e.target.checked,
                            })
                          }
                        />
                        Active
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? (
                    <Spinner size="sm" />
                  ) : currentContact.id ? (
                    "Update"
                  ) : (
                    "Create"
                  )}
                </Button>
                <Button
                  color="secondary"
                  onClick={() => setContactModal(false)}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Settings Modal */}
          <Modal
            isOpen={settingsModal}
            toggle={() => setSettingsModal(!settingsModal)}
          >
            <Form onSubmit={handleSettingsSubmit}>
              <ModalHeader toggle={() => setSettingsModal(!settingsModal)}>
                Edit App Settings
              </ModalHeader>
              <ModalBody>
                <FormGroup>
                  <Label for="help_footer_text">Footer Text</Label>
                  <Input
                    type="text"
                    id="help_footer_text"
                    value={appSettings.help_footer_text}
                    onChange={(e) =>
                      setAppSettings({
                        ...appSettings,
                        help_footer_text: e.target.value,
                      })
                    }
                    placeholder="Tafaseel - Connecting Influencers, Businesses & Customers"
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="app_version">App Version</Label>
                  <Input
                    type="text"
                    id="app_version"
                    value={appSettings.app_version}
                    onChange={(e) =>
                      setAppSettings({
                        ...appSettings,
                        app_version: e.target.value,
                      })
                    }
                    placeholder="Version 1.0.0"
                  />
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? <Spinner size="sm" /> : "Update Settings"}
                </Button>
                <Button
                  color="secondary"
                  onClick={() => setSettingsModal(false)}
                >
                  Cancel
                </Button>
              </ModalFooter>
            </Form>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HelpSupport;
